import React, { useState, useEffect } from 'react';
import { FileText, Download, Mail, Paperclip, Send, CheckCircle, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const steps = [
  {
    id: 1,
    title: "Fill the Form",
    description: "Complete all required fields",
    icon: FileText,
    color: "bg-blue-500",
  },
  {
    id: 2,
    title: "Click Submit",
    description: "Review and submit your application",
    icon: Send,
    color: "bg-purple-500",
  },
  {
    id: 3,
    title: "Files Download",
    description: "4 files save to your device",
    icon: Download,
    color: "bg-orange-500",
  },
  {
    id: 4,
    title: "Email Opens",
    description: "Your email client launches",
    icon: Mail,
    color: "bg-green-500",
  },
  {
    id: 5,
    title: "Attach Files",
    description: "Add the downloaded files",
    icon: Paperclip,
    color: "bg-pink-500",
  },
  {
    id: 6,
    title: "Send Email",
    description: "Submit your application!",
    icon: CheckCircle,
    color: "bg-emerald-500",
  },
];

export const SubmissionProcessAnimation = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <div 
      className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 md:p-8"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-4 -left-4 w-40 h-40 bg-secondary/20 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Header */}
      <div className="relative text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
          Submission Process
        </h3>
        <p className="text-slate-400 text-sm">
          Watch how the application submission works
        </p>
      </div>

      {/* Main Animation Area */}
      <div className="relative flex flex-col items-center justify-center min-h-[200px] mb-6">
        {/* Active Step Display */}
        <div className="relative">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === activeStep;
            
            return (
              <div
                key={step.id}
                className={cn(
                  "absolute inset-0 flex flex-col items-center justify-center transition-all duration-500",
                  isActive 
                    ? "opacity-100 scale-100 translate-y-0" 
                    : "opacity-0 scale-75 translate-y-4 pointer-events-none"
                )}
              >
                {/* Animated Icon Container */}
                <div className={cn(
                  "relative w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center mb-4 transition-all duration-500",
                  step.color,
                  isActive && "animate-bounce"
                )}>
                  {/* Pulse rings */}
                  <div className={cn(
                    "absolute inset-0 rounded-full animate-ping opacity-25",
                    step.color
                  )} />
                  <div className={cn(
                    "absolute inset-2 rounded-full animate-pulse opacity-50",
                    step.color
                  )} />
                  
                  <Icon className="w-10 h-10 md:w-14 md:h-14 text-white relative z-10" />
                </div>

                {/* Step Text */}
                <h4 className="text-xl md:text-2xl font-bold text-white mb-1">
                  {step.title}
                </h4>
                <p className="text-slate-400 text-sm md:text-base">
                  {step.description}
                </p>
              </div>
            );
          })}
          
          {/* Placeholder for layout */}
          <div className="opacity-0 pointer-events-none flex flex-col items-center">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full mb-4" />
            <h4 className="text-xl md:text-2xl font-bold mb-1">Placeholder</h4>
            <p className="text-sm md:text-base">Description here</p>
          </div>
        </div>
      </div>

      {/* Step Indicators */}
      <div className="relative flex items-center justify-center gap-2 md:gap-3">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isPast = index < activeStep;
          
          return (
            <React.Fragment key={step.id}>
              <button
                onClick={() => setActiveStep(index)}
                className={cn(
                  "relative w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer",
                  isActive 
                    ? `${step.color} scale-110 shadow-lg` 
                    : isPast
                      ? "bg-slate-600"
                      : "bg-slate-700 hover:bg-slate-600"
                )}
                title={step.title}
              >
                <Icon className={cn(
                  "w-4 h-4 md:w-5 md:h-5 transition-colors",
                  isActive || isPast ? "text-white" : "text-slate-400"
                )} />
                
                {isActive && (
                  <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping" />
                )}
              </button>
              
              {index < steps.length - 1 && (
                <ArrowRight className={cn(
                  "w-4 h-4 transition-colors hidden md:block",
                  index < activeStep ? "text-slate-500" : "text-slate-600"
                )} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress Bar */}
      <div className="relative mt-6 h-1 bg-slate-700 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500 rounded-full"
          style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
        />
      </div>

      {/* Instruction */}
      <p className="text-center text-slate-500 text-xs mt-4">
        {isPaused ? "Click a step to navigate • Move mouse away to resume" : "Hover to pause • Click steps to navigate"}
      </p>
    </div>
  );
};

export default SubmissionProcessAnimation;
